// View 2 — Invite user drawer.
//
// Per design/UI : UX v1.0/for-engineering-users/specs/users-strings.md.
// Layout: email + first + last + role radio cards + region (locked) +
// granted-permissions preview chips + footer (Cancel / Send invitation).

import { useMemo, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Drawer,
  RoleBadge,
  Spinner,
  TextInput,
} from '@gc-coach/design-system';
import {
  ADMIN_INVITE_ALLOWED_DOMAINS,
  ALL_ROLES,
  isAdminInviteEmailAllowed,
  permissionsForRoles,
  ROLE_PERMISSIONS,
  type RoleKey,
} from '@gc-coach/shared';
import { inviteUser } from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import { roleLabel } from '../shell/roleLabels.js';
import styles from './InviteUserDrawer.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Human-readable domain list for placeholders + error copy. Reads from
// the shared allowlist so adding a partner firm is a single-line edit
// in packages/shared/src/admin-invite.ts.
const ALLOWED_DOMAINS_DISPLAY = ADMIN_INVITE_ALLOWED_DOMAINS.map((d) => `@${d}`).join(', ');

export interface InviteUserDrawerProps {
  onClose: () => void;
  onInvited: () => void;
}

export function InviteUserDrawer({ onClose, onInvited }: InviteUserDrawerProps) {
  const { t, tt, lang } = useI18n();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<RoleKey>('content_editor');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);

  const grantedPerms = useMemo(() => permissionsForRoles([role]), [role]);

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) errs['email'] = t('users.inviteErrorRequired');
    else if (!EMAIL_RE.test(trimmed)) errs['email'] = t('users.inviteErrorEmailFormat');
    else if (!isAdminInviteEmailAllowed(trimmed))
      errs['email'] = tt('users.inviteErrorEmailDomainTemplate', { domains: ALLOWED_DOMAINS_DISPLAY });
    if (!firstName.trim()) errs['firstName'] = t('users.inviteErrorRequired');
    if (!lastName.trim()) errs['lastName'] = t('users.inviteErrorRequired');
    return errs;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setTopError(null);
    const result = await inviteUser({
      email: email.trim().toLowerCase(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role,
      region: 'eu-west-1',
      // Render the invite email in the inviter's current UI language.
      // Backend defaults to 'en' if omitted; we send it explicitly.
      locale: lang === 'FR' ? 'fr' : 'en',
    });
    setSubmitting(false);

    if (result.ok) {
      onInvited();
      return;
    }
    if (result.code === 'BAD_REQUEST' && /already exists/i.test(result.message)) {
      setFieldErrors({ email: t('users.inviteErrorDuplicate') });
      return;
    }
    if (result.code === 'VALIDATION_FAILED') {
      // Server returned per-field errors — surface them rather than the
      // generic message.
      const generic = t('users.inviteErrorGeneric');
      setTopError(result.message || generic);
      return;
    }
    setTopError(t('users.inviteErrorGeneric'));
  }

  return (
    <Drawer
      open
      onClose={submitting ? () => undefined : onClose}
      title={t('users.inviteTitle')}
      footer={
        <div className={styles.footer}>
          <Button kind="ghost" onClick={onClose} disabled={submitting}>
            {t('users.cancel')}
          </Button>
          <Button kind="primary" type="submit" form="invite-user-form" disabled={submitting}>
            {submitting ? <Spinner /> : t('users.sendInvite')}
          </Button>
        </div>
      }
    >
      <form id="invite-user-form" className={styles.form} onSubmit={onSubmit} noValidate>
        <p className={styles.intro}>{t('users.inviteIntro')}</p>

        {topError && (
          <div className={styles.alert}>
            <Alert severity="danger">{topError}</Alert>
          </div>
        )}

        <TextInput
          label={t('users.fieldEmail')}
          type="email"
          placeholder={tt('users.fieldEmailPlaceholderTemplate', { firstDomain: ADMIN_INVITE_ALLOWED_DOMAINS[0] })}
          hint={tt('users.fieldEmailHintTemplate', { domains: ALLOWED_DOMAINS_DISPLAY })}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          errorMessage={fieldErrors['email']}
          autoComplete="off"
          required
        />

        <div className={styles.row2}>
          <TextInput
            label={t('users.fieldFirstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            errorMessage={fieldErrors['firstName']}
            autoComplete="given-name"
            required
          />
          <TextInput
            label={t('users.fieldLastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            errorMessage={fieldErrors['lastName']}
            autoComplete="family-name"
            required
          />
        </div>

        <fieldset className={styles.roleGroup}>
          <legend className={styles.legend}>{t('users.fieldRole')}</legend>
          <div className={styles.roleCards}>
            {ALL_ROLES.map((r) => (
              <RoleCard
                key={r}
                role={r}
                checked={role === r}
                label={roleLabel(r, t)}
                description={t(`roleDescriptions.${r}`)}
                onChange={() => setRole(r)}
              />
            ))}
          </div>
        </fieldset>

        <div>
          <span className={styles.legend}>{t('users.fieldRegion')}</span>
          <div className={styles.regionLocked}>{t('users.regionEuW1')}</div>
        </div>

        <div className={styles.permPreview}>
          <span className={styles.legend}>{t('users.permissionPreview')}</span>
          <div className={styles.permChips}>
            {grantedPerms.map((perm) => (
              <span key={perm} className={styles.permChip}>
                {t(`permissionLabels.${perm}`)}
                <code className={styles.permKey}>{perm}</code>
              </span>
            ))}
            {ROLE_PERMISSIONS[role].length === 0 && (
              <span className={styles.permEmpty}>—</span>
            )}
          </div>
        </div>
      </form>
    </Drawer>
  );
}

// ─── Role radio card ─────────────────────────────────────────────────────────

function RoleCard({
  role,
  checked,
  label,
  description,
  onChange,
}: {
  role: RoleKey;
  checked: boolean;
  label: string;
  description: string;
  onChange: () => void;
}) {
  return (
    <label className={[styles.roleCard, checked ? styles.roleCardActive : null].filter(Boolean).join(' ')}>
      <input
        type="radio"
        name="role"
        value={role}
        checked={checked}
        onChange={onChange}
        className={styles.roleRadio}
      />
      <span className={styles.roleCardHead}>
        <RoleBadge role={role} label={label} />
      </span>
      <span className={styles.roleCardDesc}>{description}</span>
    </label>
  );
}
